import { useState } from "react";
import {
  App,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AdminUser } from "@glow-price/shared";
import { ADMIN_ROLES, ENTITY_STATUSES } from "@glow-price/shared";

import { PlusOutlined } from "@ant-design/icons";

import { adminUsersApi, rolesApi } from "../api/resources";
import { useCurrentAdmin } from "../auth/useCurrentAdmin";
import { EmptyState, EnhancedTable, PageHeader } from "../components";

const STATUS_OPTIONS = Object.values(ENTITY_STATUSES).map((v) => ({
  value: v,
  label: v,
}));

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

type CreateFormValues = {
  username: string;
  email?: string;
  role: string;
  password: string;
};

type EditFormValues = {
  email?: string | null;
  role: string;
  status: AdminUser["status"];
};

export function AdminUsersPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const me = useCurrentAdmin();
  const isSuperadmin = me?.role === ADMIN_ROLES.SUPERADMIN;

  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [resetting, setResetting] = useState<AdminUser | null>(null);

  const [createForm] = Form.useForm<CreateFormValues>();
  const [editForm] = Form.useForm<EditFormValues>();
  const [resetForm] = Form.useForm<{ newPassword: string }>();

  const list = useQuery({
    queryKey: ["admin-users", page],
    queryFn: () => adminUsersApi.list({ page, pageSize: 20 }),
  });

  // DB-driven roles — drives the role Select options and the table's slug→中文名 lookup.
  const rolesQuery = useQuery({
    queryKey: ["roles"],
    queryFn: () => rolesApi.list(),
  });
  const roles = rolesQuery.data?.items ?? [];
  const roleOptions = roles.map((r) => ({ value: r.slug, label: r.nameCn }));
  const roleLabel = (slug: string) =>
    roles.find((r) => r.slug === slug)?.nameCn ?? slug;

  const createMutation = useMutation({
    mutationFn: (values: CreateFormValues) =>
      adminUsersApi.create({
        username: values.username,
        email: values.email ?? null,
        role: values.role,
        password: values.password,
      }),
    onSuccess: () => {
      message.success("已创建");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      setCreateOpen(false);
      createForm.resetFields();
    },
    onError: (err: Error) => message.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; values: EditFormValues }) =>
      adminUsersApi.update(input.id, {
        email: input.values.email ?? null,
        role: input.values.role,
        status: input.values.status,
      }),
    onSuccess: () => {
      message.success("已更新");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      setEditing(null);
      editForm.resetFields();
    },
    onError: (err: Error) => message.error(err.message),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (input: { id: string; newPassword: string }) =>
      adminUsersApi.resetPassword(input.id, input.newPassword),
    onSuccess: () => {
      message.success("密码已重置");
      setResetting(null);
      resetForm.resetFields();
    },
    onError: (err: Error) => message.error(err.message),
  });

  const unlockMutation = useMutation({
    mutationFn: (id: string) => adminUsersApi.unlock(id),
    onSuccess: () => {
      message.success("已解锁");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminUsersApi.remove(id),
    onSuccess: () => {
      message.success("已删除");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  return (
    <div>
      <PageHeader
        title="账号管理"
        description="管理后台管理员账号与角色权限（超管 / 编辑 / 只读）。仅超级管理员可新建。"
        stats={[{ label: `共 ${list.data?.total ?? 0} 个账号` }]}
        extra={
          isSuperadmin ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                createForm.resetFields();
                setCreateOpen(true);
              }}
            >
              新建管理员
            </Button>
          ) : null
        }
      />

      {!list.isLoading && (list.data?.items?.length ?? 0) === 0 ? (
        <EmptyState
          title="暂无管理员"
          description={
            isSuperadmin
              ? "尚未创建任何管理员账号，点击右上角「新建管理员」开始添加。"
              : "尚未创建任何管理员账号。"
          }
        />
      ) : (
      <EnhancedTable<AdminUser>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data?.items ?? []}
        persistKey="admin-users"
        exportFilename="admin-users"
        pagination={{
          current: page,
          pageSize: 20,
          total: list.data?.total ?? 0,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        columns={[
          { title: "用户名", dataIndex: "username", width: 160 },
          {
            title: "邮箱",
            dataIndex: "email",
            render: (v: string | null) => v ?? "—",
          },
          {
            title: "角色",
            dataIndex: "role",
            width: 110,
            render: (v: string) => (
              <Tag color={v === ADMIN_ROLES.SUPERADMIN ? "red" : "default"}>
                {roleLabel(v)}
              </Tag>
            ),
          },
          { title: "状态", dataIndex: "status", width: 90 },
          {
            title: "最后登录",
            dataIndex: "lastLoginAt",
            width: 180,
            render: (v: string | null) => formatDate(v),
          },
          {
            title: "失败次数",
            dataIndex: "failedLoginCount",
            width: 90,
            align: "right" as const,
          },
          {
            title: "锁定",
            dataIndex: "lockedAt",
            width: 90,
            render: (v: string | null) =>
              v ? <Tag color="orange">已锁定</Tag> : <Tag>正常</Tag>,
          },
          ...(isSuperadmin
            ? [
                {
                  title: "操作",
                  width: 260,
                  render: (_: unknown, row: AdminUser) => (
                    <Space size="small" wrap>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          editForm.setFieldsValue({
                            email: row.email,
                            role: row.role,
                            status: row.status,
                          });
                          setEditing(row);
                        }}
                      >
                        编辑
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          resetForm.resetFields();
                          setResetting(row);
                        }}
                      >
                        重置密码
                      </Button>
                      {row.lockedAt ? (
                        <Popconfirm
                          title="确认解锁？"
                          onConfirm={() => unlockMutation.mutate(row.id)}
                        >
                          <Button type="link" size="small">
                            解锁
                          </Button>
                        </Popconfirm>
                      ) : null}
                      <Popconfirm
                        title="确认删除该管理员？"
                        okType="danger"
                        onConfirm={() => deleteMutation.mutate(row.id)}
                      >
                        <Button type="link" size="small" danger>
                          删除
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />
      )}

      {/* 新建管理员 */}
      <Modal
        title="新建管理员"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={createMutation.isPending}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ role: "editor" }}
          onFinish={(values) => createMutation.mutate(values)}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: "请输入用户名" },
              { min: 3, max: 32 },
              {
                pattern: /^[A-Za-z0-9._-]+$/,
                message: "只能包含字母、数字、点、下划线、连字符",
              },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[{ type: "email", message: "邮箱格式不正确" }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>
            <Select options={roleOptions} loading={rolesQuery.isLoading} />
          </Form.Item>
          <Form.Item
            name="password"
            label="初始密码"
            rules={[
              { required: true, message: "请输入密码" },
              { min: 12, message: "密码至少 12 位" },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑管理员 */}
      <Modal
        title={editing ? `编辑：${editing.username}` : "编辑"}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={() => editForm.submit()}
        confirmLoading={updateMutation.isPending}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) =>
            editing && updateMutation.mutate({ id: editing.id, values })
          }
        >
          <Form.Item label="用户名">
            <Input value={editing?.username ?? ""} disabled />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[{ type: "email", message: "邮箱格式不正确" }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>
            <Select options={roleOptions} loading={rolesQuery.isLoading} />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true }]}>
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 重置密码 */}
      <Modal
        title={resetting ? `重置密码：${resetting.username}` : "重置密码"}
        open={!!resetting}
        onCancel={() => setResetting(null)}
        onOk={() => resetForm.submit()}
        confirmLoading={resetPasswordMutation.isPending}
        destroyOnClose
      >
        <Form
          form={resetForm}
          layout="vertical"
          onFinish={(values) =>
            resetting &&
            resetPasswordMutation.mutate({
              id: resetting.id,
              newPassword: values.newPassword,
            })
          }
        >
          <Typography.Paragraph type="secondary">
            重置后用户需在下次登录时改密
          </Typography.Paragraph>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: "请输入新密码" },
              { min: 12, message: "密码至少 12 位" },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
